import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, TrendingUp, TrendingDown, DollarSign, Calculator } from 'lucide-react'
import { useSEO } from '@/hooks/useSEO'
import { MarketingHeader } from '@/components/marketing/MarketingHeader'
import { MarketingFooter } from '@/components/marketing/MarketingFooter'

function fmt(n: number): string {
  return n.toLocaleString('en-US', { maximumFractionDigits: 0 })
}

function fmtDollar(n: number): string {
  return '$' + fmt(n)
}

export default function ROICalculator() {
  // Mobile menu state previously lived here; now owned by MarketingHeader.
  const [activeTab, setActiveTab] = useState<'sell' | 'buy'>('sell')

  // Sell-side inputs
  const [sellContracts, setSellContracts] = useState(300)
  const [sellAvgValue, setSellAvgValue] = useState(150000)
  const [sellEscPct, setSellEscPct] = useState(40)
  const [sellEscRate, setSellEscRate] = useState(2.5)
  const [sellYears, setSellYears] = useState(2)

  // Buy-side inputs
  const [buyContracts, setBuyContracts] = useState(500)
  const [buyAvgValue, setBuyAvgValue] = useState(200000)
  const [buyEscPct, setBuyEscPct] = useState(30)
  const [buyEscRate, setBuyEscRate] = useState(3)
  const [buyRenegPct, setBuyRenegPct] = useState(10)
  const [buySavingsPct, setBuySavingsPct] = useState(50)

  // Sell-side calculations
  const sellContractsWithEsc = Math.round(sellContracts * (sellEscPct / 100))
  const sellAnnualUncollected = sellContractsWithEsc * sellAvgValue * (sellEscRate / 100)
  let sellCumulative = 0
  for (let y = 1; y <= sellYears; y++) {
    sellCumulative += sellContractsWithEsc * sellAvgValue * (sellEscRate / 100) * y
  }

  // Buy-side calculations
  const buyContractsWithEsc = Math.round(buyContracts * (buyEscPct / 100))
  const buyTotalAnnualExposure = buyContractsWithEsc * buyAvgValue * (buyEscRate / 100)
  const buyContractsRenegotiated = Math.round(buyContractsWithEsc * (buyRenegPct / 100))
  const buyAnnualSavings = buyContractsRenegotiated * buyAvgValue * (buyEscRate / 100) * (buySavingsPct / 100)
  const buyThreeYearSavings = buyAnnualSavings * 3

  // Combined
  const documentiqAnnualCost = 49 * 12
  const combinedAnnualValue = sellAnnualUncollected + buyAnnualSavings
  const roiMultiple = combinedAnnualValue > 0 ? Math.round(combinedAnnualValue / documentiqAnnualCost) : 0

  useSEO({
    title: 'Contract Escalation ROI Calculator',
    description:
      'Calculate how much revenue you are leaving on the table from unapplied customer contract escalation clauses and how much you could save on supplier escalations.',
    canonical: 'https://documentiq.algoscale.com/roi-calculator',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebApplication',
        name: 'Contract Escalation ROI Calculator',
        description:
          'Free tool to calculate revenue recovery from sell-side contract escalations and cost savings from buy-side supplier escalations.',
        applicationCategory: 'FinanceApplication',
        operatingSystem: 'Web',
        url: 'https://documentiq.algoscale.com/roi-calculator',
        browserRequirements: 'Requires JavaScript',
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
        },
        creator: {
          '@type': 'Organization',
          name: 'Algoscale',
          url: 'https://algoscale.com',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://documentiq.algoscale.com' },
          { '@type': 'ListItem', position: 2, name: 'ROI Calculator', item: 'https://documentiq.algoscale.com/roi-calculator' },
        ],
      },
    ],
  })

  return (
    <div className="min-h-screen bg-white text-gray-900 antialiased">
      <MarketingHeader />

      {/* ── Hero ───────────────────────────────────────────────── */}
      <section className="py-16 sm:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 text-sm font-medium mb-6">
            <Calculator size={16} />
            Interactive ROI Calculator
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-gray-900 leading-tight">
            Contract Escalation ROI Calculator
          </h1>
          <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
            Calculate how much revenue you're leaving on the table from unapplied customer
            escalations &mdash; and how much you could save on supplier escalations.
          </p>
        </div>
      </section>

      {/* ── Calculator ─────────────────────────────────────────── */}
      <section className="py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Tabs */}
          <div className="flex border-b border-gray-200 mb-8">
            <button
              onClick={() => setActiveTab('sell')}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'sell'
                  ? 'border-green-600 text-green-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <TrendingUp size={18} />
              Sell-Side (Revenue Recovery)
            </button>
            <button
              onClick={() => setActiveTab('buy')}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'buy'
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <TrendingDown size={18} />
              Buy-Side (Cost Savings)
            </button>
          </div>

          {/* Sell-Side Tab */}
          {activeTab === 'sell' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Inputs */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6 sm:p-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Your Customer Contracts</h3>
                <div className="space-y-5">
                  <InputField
                    label="Number of customer contracts"
                    value={sellContracts}
                    onChange={setSellContracts}
                    min={1}
                    step={10}
                  />
                  <InputField
                    label="Average annual contract value"
                    value={sellAvgValue}
                    onChange={setSellAvgValue}
                    min={0}
                    step={5000}
                    prefix="$"
                  />
                  <InputField
                    label="% of contracts with escalation clauses"
                    value={sellEscPct}
                    onChange={setSellEscPct}
                    min={0}
                    max={100}
                    step={5}
                    suffix="%"
                  />
                  <InputField
                    label="Average annual escalation rate"
                    value={sellEscRate}
                    onChange={setSellEscRate}
                    min={0}
                    max={50}
                    step={0.5}
                    suffix="%"
                    decimals
                  />
                  <InputField
                    label="Years of uncollected escalations"
                    value={sellYears}
                    onChange={setSellYears}
                    min={1}
                    max={10}
                    step={1}
                  />
                </div>
              </div>

              {/* Results */}
              <div className="space-y-4">
                <div className="bg-green-50 rounded-2xl border border-green-200 p-6 sm:p-8">
                  <h3 className="text-lg font-semibold text-green-900 mb-6">Revenue Recovery Opportunity</h3>
                  <div className="space-y-4">
                    <ResultRow label="Contracts with escalation clauses" value={fmt(sellContractsWithEsc)} />
                    <ResultRow label="Annual revenue not collected" value={fmtDollar(sellAnnualUncollected)} highlight />
                    <ResultRow
                      label={`Cumulative unrealized revenue (${sellYears} yr${sellYears > 1 ? 's' : ''})`}
                      value={fmtDollar(sellCumulative)}
                      highlight
                      large
                    />
                  </div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p className="text-sm text-amber-800 font-medium">
                    This is revenue you are contractually entitled to but not collecting.
                    Every month without action, the gap grows.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Buy-Side Tab */}
          {activeTab === 'buy' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Inputs */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6 sm:p-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Your Supplier Contracts</h3>
                <div className="space-y-5">
                  <InputField
                    label="Number of supplier contracts"
                    value={buyContracts}
                    onChange={setBuyContracts}
                    min={1}
                    step={10}
                  />
                  <InputField
                    label="Average annual contract value"
                    value={buyAvgValue}
                    onChange={setBuyAvgValue}
                    min={0}
                    step={5000}
                    prefix="$"
                  />
                  <InputField
                    label="% of contracts with escalation clauses"
                    value={buyEscPct}
                    onChange={setBuyEscPct}
                    min={0}
                    max={100}
                    step={5}
                    suffix="%"
                  />
                  <InputField
                    label="Average annual escalation rate"
                    value={buyEscRate}
                    onChange={setBuyEscRate}
                    min={0}
                    max={50}
                    step={0.5}
                    suffix="%"
                    decimals
                  />
                  <InputField
                    label="% of escalations you could renegotiate"
                    value={buyRenegPct}
                    onChange={setBuyRenegPct}
                    min={0}
                    max={100}
                    step={5}
                    suffix="%"
                  />
                  <InputField
                    label="Average savings per renegotiated escalation"
                    value={buySavingsPct}
                    onChange={setBuySavingsPct}
                    min={0}
                    max={100}
                    step={5}
                    suffix="%"
                  />
                </div>
              </div>

              {/* Results */}
              <div className="bg-blue-50 rounded-2xl border border-blue-200 p-6 sm:p-8">
                <h3 className="text-lg font-semibold text-blue-900 mb-6">Cost Savings Opportunity</h3>
                <div className="space-y-4">
                  <ResultRow label="Contracts with escalation clauses" value={fmt(buyContractsWithEsc)} />
                  <ResultRow label="Total annual escalation exposure" value={fmtDollar(buyTotalAnnualExposure)} />
                  <ResultRow label="Contracts you could renegotiate" value={fmt(buyContractsRenegotiated)} />
                  <ResultRow label="Potential annual savings" value={fmtDollar(buyAnnualSavings)} highlight />
                  <ResultRow label="3-year savings projection" value={fmtDollar(buyThreeYearSavings)} highlight large />
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── Combined Summary ───────────────────────────────────── */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-10">
            Your Combined ROI Summary
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <SummaryCard
              icon={<TrendingUp size={24} className="text-green-600" />}
              label="Sell-Side Revenue Recovery"
              sublabel="Annual"
              value={fmtDollar(sellAnnualUncollected)}
              color="green"
            />
            <SummaryCard
              icon={<TrendingDown size={24} className="text-blue-600" />}
              label="Buy-Side Cost Savings"
              sublabel="Annual"
              value={fmtDollar(buyAnnualSavings)}
              color="blue"
            />
            <SummaryCard
              icon={<DollarSign size={24} className="text-purple-600" />}
              label="Combined Annual Value"
              sublabel="Revenue + Savings"
              value={fmtDollar(combinedAnnualValue)}
              color="purple"
            />
            <SummaryCard
              icon={<Calculator size={24} className="text-amber-600" />}
              label="Estimated ROI"
              sublabel="vs $49/mo DocumentIQ"
              value={`${fmt(roiMultiple)}x`}
              color="amber"
            />
          </div>
          <div className="mt-8 bg-white rounded-2xl border border-gray-200 p-6 sm:p-8 text-center">
            <p className="text-gray-600 text-sm mb-1">
              DocumentIQ costs <span className="font-semibold text-gray-900">$49/month</span> ({fmtDollar(documentiqAnnualCost)}/year).
            </p>
            <p className="text-xl sm:text-2xl font-bold text-gray-900">
              Your estimated ROI is{' '}
              <span className="text-blue-600">{fmt(roiMultiple)}x</span>
              {' '}&mdash;{' '}
              <span className="text-green-600">{fmtDollar(combinedAnnualValue)}</span> in annual value.
            </p>
          </div>
        </div>
      </section>

      {/* ── CTA ────────────────────────────────────────────────── */}
      <section className="py-16 sm:py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
            Ready to find the escalation clauses in your contracts?
          </h2>
          <p className="mt-4 text-lg text-gray-600">
            Upload your contracts and let DocumentIQ automatically identify, extract, and track every
            price escalation clause across your portfolio.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm"
            >
              Start for Free
              <ArrowRight size={16} />
            </Link>
            <a
              href="mailto:askus@algoscale.com"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Talk to Sales
            </a>
          </div>
        </div>
      </section>

      {/* ── Related Resources ──────────────────────────────────── */}
      <section className="py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-10">Related Resources</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <ResourceCard
              type="Blog"
              title="The Hidden Cost of Missing Price Escalation Clauses"
              href="/blog/hidden-cost-of-missing-price-escalation-clauses"
            />
            <ResourceCard
              type="Solution"
              title="Contract Intelligence"
              href="/solutions/function/contract-intelligence"
            />
            <ResourceCard
              type="Case Study"
              title="Contract Price Escalation"
              href="/case-studies/contract-price-escalation"
            />
          </div>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────────────── */}
      <MarketingFooter />
    </div>
  )
}

/* ── Sub-components ──────────────────────────────────────────── */

interface InputFieldProps {
  label: string
  value: number
  onChange: (v: number) => void
  min?: number
  max?: number
  step?: number
  prefix?: string
  suffix?: string
  decimals?: boolean
}

function InputField({ label, value, onChange, min, max, step = 1, prefix, suffix, decimals }: InputFieldProps) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
      <div className="relative">
        {prefix && (
          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400 text-sm pointer-events-none">
            {prefix}
          </span>
        )}
        <input
          type="number"
          value={value}
          onChange={(e) => {
            const v = decimals ? parseFloat(e.target.value) : parseInt(e.target.value, 10)
            if (!isNaN(v)) onChange(v)
          }}
          min={min}
          max={max}
          step={step}
          className={`w-full rounded-lg border border-gray-300 bg-white py-2.5 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-colors ${
            prefix ? 'pl-7' : 'pl-3'
          } ${suffix ? 'pr-10' : 'pr-3'}`}
        />
        {suffix && (
          <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 text-sm pointer-events-none">
            {suffix}
          </span>
        )}
      </div>
    </div>
  )
}

interface ResultRowProps {
  label: string
  value: string
  highlight?: boolean
  large?: boolean
}

function ResultRow({ label, value, highlight, large }: ResultRowProps) {
  return (
    <div className={`flex items-center justify-between py-3 ${large ? 'border-t border-gray-200 pt-4 mt-2' : ''}`}>
      <span className={`text-sm ${highlight ? 'font-medium text-gray-900' : 'text-gray-600'}`}>{label}</span>
      <span className={`font-semibold ${large ? 'text-xl sm:text-2xl text-gray-900' : highlight ? 'text-lg text-gray-900' : 'text-sm text-gray-800'}`}>
        {value}
      </span>
    </div>
  )
}

interface SummaryCardProps {
  icon: React.ReactNode
  label: string
  sublabel: string
  value: string
  color: 'green' | 'blue' | 'purple' | 'amber'
}

function SummaryCard({ icon, label, sublabel, value, color }: SummaryCardProps) {
  const bgMap = {
    green: 'bg-green-50 border-green-200',
    blue: 'bg-blue-50 border-blue-200',
    purple: 'bg-purple-50 border-purple-200',
    amber: 'bg-amber-50 border-amber-200',
  }
  return (
    <div className={`rounded-2xl border p-5 ${bgMap[color]}`}>
      <div className="mb-3">{icon}</div>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</p>
      <p className="text-xs text-gray-400 mb-2">{sublabel}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
  )
}

interface ResourceCardProps {
  type: string
  title: string
  href: string
}

function ResourceCard({ type, title, href }: ResourceCardProps) {
  return (
    <Link
      to={href}
      className="group flex flex-col rounded-2xl border border-gray-200 bg-white hover:border-blue-200 hover:shadow-lg hover:shadow-blue-50 transition-all duration-300 p-6"
    >
      <span className="inline-flex self-start px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium mb-3">
        {type}
      </span>
      <h3 className="text-base font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
        {title}
      </h3>
      <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-blue-600 group-hover:gap-2 transition-all">
        Read more <ArrowRight size={14} />
      </span>
    </Link>
  )
}
